import axios from 'axios';
import type { DateTime } from 'luxon';
import { merge } from 'lodash';

/**
 *LinksService
 */
export default class LinksService
{
    public static path = 'admin/wk/links';

    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(this.path, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`${this.path}/${id}`)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(this.path, model)).data;
    }

    /**
     * @param id number
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/${id}`, model)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`${this.path}/${id}`)).data as Statement;
    }

    /**
     * @param id number
     *
     * @returns Promise<LinkifyArticleModel>
    */
    public static async linkifyArticle(model: LinkifyArticleModel): Promise<LinkifyArticleModel>
    {
        return (await axios.post<LinkifyArticleModel>(`admin/wk/links/linkify-article`, model)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async getReport(id: number, polishUtcOffsetHours: number): Promise<Blob>
    {
        return (await axios.get<Blob>(`admin/wk/links/report`,
            { params: { id, polishUtcOffsetHours }, responseType: 'blob' })).data;
    }

    public static async checkURL(model: LinkCheckModel): Promise<UrlItemModel>
    {
        const raw: any = (await axios.post('admin/wk/links/check', model)).data;
        const v = raw?.value ?? raw?.p0 ?? raw;

        return {
            url: v?.url ?? '',
            status: v?.status ?? 'Error',
            httpStatus: v?.httpStatus,
            redirectLocation: v?.redirectLocation,
            error: v?.error
        } as UrlItemModel;
    }
}

export interface ListItemModel
{
    id: number;
    keyword: string;
    link: string;
    sectionId?: number;
    sectionName: string;
    count: number;
    status: string;
    dateCreatedUtc: DateTime;
    startDateUtc: DateTime;
    endDateUtc: DateTime;
}

export interface FormModel
{
    id: number;
    keyword: string;
    link: string;
    sectionId?: number;
    count: number;
    target: string;
    noFollow: boolean;

    status: string;
    dateCreatedUtc: DateTime;
    startDateUtc: DateTime;
    endDateUtc: DateTime;
}

export interface LinkifyArticleModel
{
    sectionId: number;
    body: string;
    found: number;
    phrases: Record<string, string>;
}

export interface FilterModel
{
    keyword: string;
    status: string;
}

export enum LinkingToolTaskStatus
{
    New = 'New',
    InProgress = 'InProgress',
    OnHold = 'OnHold',
    Completed = 'Completed'
}

export interface LinkCheckModel
{
    url: string;
}

export interface UrlItemModel {
    url: string;
    status: 'Ok' | 'Redirect' | 'Error' | 'Timeout' | 'InvalidUrl';
    httpStatus?: number;
    redirectLocation?: string;
    error?: string;
}

export const LinkingToolTaskStatusOptions = (): Option[] => [
    { value: LinkingToolTaskStatus.New, text: $t('[[[Nowe]]]') },
    { value: LinkingToolTaskStatus.InProgress, text: $t('[[[W trakcie]]]') },
    { value: LinkingToolTaskStatus.OnHold, text: $t('[[[Wstrzymane]]]') },
    { value: LinkingToolTaskStatus.Completed, text: $t('[[[Zakończone]]]') }
];

export const LinkingToolTaskStatusName = (value: string): string => LinkingToolTaskStatusOptions().find(item => item.value === value)?.text;
